import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import AutosizeInput from 'dpl/common/components/AutosizeInput';
import Select from 'dpl/form/components/Select';
import Textarea from 'dpl/form/components/Textarea';

import ProfileAvatar from './ProfileAvatar';

export default function PaymentRequestAmountQuestionV2({
  amountInDollars,
  autoFocus,
  availableCategories,
  breederProfile,
  category,
  onChange,
  puppy,
  shouldShowEditButton,
  maxAmount
}) {
  const inputInstanceRef = useRef();
  const [isInputLocked, setIsInputLocked] = useState(shouldShowEditButton);

  const datePickerStartDate = new Date();
  const amountInput = inputInstanceRef.current?.inputRef?.current;

  datePickerStartDate.setDate(datePickerStartDate.getDate() - 1);

  function handleAmountChange(e) {
    const amount = Number(e.target.value);

    if (maxAmount && amount > maxAmount) {
      return;
    }

    onChange(e);
  }

  useEffect(() => {
    if (!isInputLocked) amountInput?.focus();
  }, [isInputLocked]);

  return (
    <div className="BreederAppPaymentRequestAmountQuestionV2">
      <ProfileAvatar
        className={classnames('center', puppy ? 'mb1' : 'mb2')}
        breederUserId={breederProfile.user_id}
        breederUserInitials={breederProfile.initials}
        breederProfilePhotoUrl={breederProfile.profile_photo_url}
        puppyProfilePhotoUrl={puppy?.profile_photo_url}
        size="lg"
      />
      {puppy?.name && (
        <h2 className="BreederAppPaymentRequestAmountQuestionV2__heading tc font-sans fw-normal lh-title pv2">
          Payment for {puppy.name}
        </h2>
      )}
      <div className="mb2-lg mb4 no-outline tc nowrap overflow-hidden">
        <div>
          <span className="BreederAppPaymentRequestAmountQuestionV2__dollarSign font-serif absolute v-top">
            $
          </span>
          <AutosizeInput
            ref={inputInstanceRef}
            autoFocus={autoFocus}
            className={classnames(
              'BreederAppPaymentRequestAmountQuestionV2__amountInput font-serif bw0 relative dib fw-light bg-transparent',
              { editing: shouldShowEditButton && !isInputLocked }
            )}
            maxWidth="340px"
            name="amount_in_dollars"
            onChange={handleAmountChange}
            onKeyDown={e => {
              if (e.key === 'Enter') setIsInputLocked(true);
            }}
            placeholder="0"
            type="number"
            value={amountInDollars || ''}
            disabled={isInputLocked}
          />
        </div>
        {shouldShowEditButton && (
          <button
            className="BreederAppPaymentRequestAmountQuestionV2__editButton button mt4 mb6 font-16"
            type="button"
            onClick={() => {
              setIsInputLocked(!isInputLocked);
            }}
          >
            {isInputLocked ? 'Edit amount' : 'Update amount'}
          </button>
        )}
      </div>
      <Select
        className="bg-white br3 w-100 ph4 pv3 mt1 b--default font-16"
        label="Category"
        name="category"
        options={availableCategories}
        onChange={onChange}
      />
      {category && (
        <div className="mt8">
          <Textarea
            className="bg-white br3 w-100 mt1 ph4 pv3 nb1 b--default input-reset Textarea__input no-resize font-16"
            rows={2}
            label="Personal message (optional)"
            name="personal_message"
            placeholder="Type your message"
            onChange={onChange}
          />
        </div>
      )}
    </div>
  );
}

PaymentRequestAmountQuestionV2.propTypes = {
  amountInDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autoFocus: PropTypes.bool,
  availableCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
  breederProfile: PropTypes.shape({
    user_id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    initials: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string.isRequired
  }).isRequired,
  category: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  puppy: PropTypes.shape({
    name: PropTypes.string.isRequired,
    profile_photo_url: PropTypes.string.isRequired
  }),
  shouldShowEditButton: PropTypes.bool,
  maxAmount: PropTypes.number
};

PaymentRequestAmountQuestionV2.defaultProps = {
  amountInDollars: null,
  autoFocus: false,
  category: null,
  puppy: null,
  shouldShowEditButton: false,
  maxAmount: null
};
